import { fetcher } from '@/config/api';

export type RequestStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

export interface PermissionRequest {
  id: string;
  user_id: string;
  requester_id: string;
  permission: string;
  justification: string;
  status: RequestStatus;
  requested_at: string;
  manager_id?: string | null;
  business_unit?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  approval_note?: string | null;
  denied_by?: string | null;
  denied_at?: string | null;
  denial_reason?: string | null;
}

export interface PermissionRequestCreate {
  permission: string;
  justification: string;
}

export interface PermissionRequestApprove {
  note?: string;
}

export interface PermissionRequestDeny {
  reason: string;
}

export interface PermissionRequestsResponse {
  requests: PermissionRequest[];
  total: number;
}

/**
 * List all permission requests (role-based filtering applied on backend)
 * is_admin sees all, Manager sees direct reports + own, User sees own
 */
export interface PermissionRequestsGroupedResponse {
  groups: Array<{
    user_id: string;
    user_name?: string;
    user_email?: string;
    business_unit?: string;
    pending_count: number;
    latest_requested_at?: string;
    requests: PermissionRequest[];
  }>;
  total: number;
}

export async function listPermissionRequests(status?: RequestStatus, groupBy?: 'user'):
  Promise<PermissionRequestsResponse | PermissionRequestsGroupedResponse> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (groupBy) {
    params.append('group_by', groupBy);
  }
  const url = `/api/permission-requests${params.toString() ? '?' + params.toString() : ''}`;
  const res = await fetcher<any>(url);

  // If grouped response
  if (res && Array.isArray(res.groups)) {
    return res as PermissionRequestsGroupedResponse;
  }

  // Backend may return either { requests: [...] } or directly an array of requests
  if (Array.isArray(res)) {
    return { requests: res as PermissionRequest[], total: (res as any).length || res.length };
  }
  if (res && Array.isArray(res.requests)) {
    return { requests: res.requests as PermissionRequest[], total: (res.total as number) || res.requests.length };
  }
  // Fallback: return empty
  return { requests: [], total: 0 };
}

export async function getPendingPermissionRequestsCount(isAdminOrManager: boolean): Promise<number> {
  const res = await listPermissionRequests('pending', isAdminOrManager ? 'user' : undefined);

  if ('groups' in res && Array.isArray(res.groups)) {
    return res.groups.reduce((total, group) => {
      if (typeof group.pending_count === 'number') {
        return total + group.pending_count;
      }

      // Fallback for inconsistent payloads that only return raw requests.
      const pendingInGroup = Array.isArray(group.requests)
        ? group.requests.filter((request) => request.status === 'pending').length
        : 0;
      return total + pendingInGroup;
    }, 0);
  }

  if ('requests' in res && Array.isArray(res.requests)) {
    return res.requests.filter((request) => request.status === 'pending').length;
  }

  return 0;
}

/**
 * Create a new permission request
 */
export async function createPermissionRequest(data: PermissionRequestCreate): Promise<PermissionRequest> {
  return fetcher<PermissionRequest>('/api/permission-requests', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Approve a permission request (is_admin or Manager only)
 */
export async function approvePermissionRequest(
  requestId: string,
  data?: PermissionRequestApprove
): Promise<PermissionRequest> {
  return fetcher<PermissionRequest>(`/api/permission-requests/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data || {})
  });
}

/**
 * Deny a permission request (is_admin or Manager only)
 */
export async function denyPermissionRequest(
  requestId: string,
  data: PermissionRequestDeny
): Promise<PermissionRequest> {
  return fetcher<PermissionRequest>(`/api/permission-requests/${requestId}/deny`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Cancel your own permission request (requester only, pending only)
 */
export async function cancelPermissionRequest(requestId: string): Promise<PermissionRequest> {
  return fetcher<PermissionRequest>(`/api/permission-requests/${requestId}`, {
    method: 'DELETE'
  });
}
